import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { environment } from '@env/environment';

export interface GlobalAiChatConfig {
  environment: string;
  agentId: string;
  apiBaseUrl: string;  // Required - no fallback
  content?: string;
  lang: string;  // Always "en"
  contentLang: string;  // Always "en"
  role: 'child' | 'executive';  // Default from environment.ai_chat_role
  autoStart: boolean;  // Always true for sidebar (connect once)
  muteAi: boolean;  // Mute when in listener mode or switching
  debug: boolean;
  fullMode: boolean;
  showWidget: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class GlobalAiChatService {
  // Get apiBaseUrl (return empty string if not configured – AI chat will be disabled)
  private getApiBaseUrl(): string {
    return environment.ai_api_baseurl_chat || '';
  }

  private defaultConfig: GlobalAiChatConfig = {
    environment: "webcomponent", // so we no that the data comes from a webcomponent in react ai-chat
    agentId: 'agent.listener',  // ← Start with listener agent
    apiBaseUrl: this.getApiBaseUrl(),
    lang: 'en',  // ← Always "en"
    contentLang: 'en',  // ← Always "en"
    role: (environment.ai_chat_role as 'child' | 'executive') || 'executive',  // ← From environment, default 'executive'
    autoStart: true,  // ← Always true - connect once at startup
    muteAi: true,  // ← Mute in listener mode (silent agent)
    debug: true,
    fullMode: true,
    showWidget: true
  };

  // Current widget configuration (observable)
  private configSubject = new BehaviorSubject<GlobalAiChatConfig>(this.defaultConfig);
  public config$: Observable<GlobalAiChatConfig> = this.configSubject.asObservable();

  constructor() {
    console.log('🎯 GlobalAiChatService initialized with listener agent');
    console.log('📍 API Base URL:', this.defaultConfig.apiBaseUrl);
    console.log('🔇 Listener mode: Muted (silent agent)');
  }

  /**
   * Update widget configuration (called by step components)
   * Simplified: Directly applies config without listener transfer
   * Disconnect/reconnect is handled by layout component on step changes
   */
  updateConfig(config: Partial<GlobalAiChatConfig>): void {
    const currentConfig = this.configSubject.value;
    
    // Determine if we should mute based on agent
    const shouldMute = this.shouldMuteForAgent(config.agentId || currentConfig.agentId);
    
    // Ensure lang and contentLang are always "en"
    const newConfig: GlobalAiChatConfig = {
      ...currentConfig,
      ...config,
      lang: 'en',  // ← Always "en"
      contentLang: 'en',  // ← Always "en"
      role: config.role || (environment.ai_chat_role as 'child' | 'executive') || 'executive',  // ← From environment, default 'executive'
      autoStart: true,  // ← Always true (will reconnect)
      muteAi: config.muteAi !== undefined ? config.muteAi : shouldMute,
      apiBaseUrl: config.apiBaseUrl || currentConfig.apiBaseUrl
    };
    
    // Warn if apiBaseUrl is not set (AI chat will not function)
    if (!newConfig.apiBaseUrl) {
      console.warn('⚠️ ai_api_baseurl_chat is not configured – AI chat disabled');
    }
    
    console.log('✅ Global AI Chat config updated:', {
      agentId: newConfig.agentId,
      muteAi: newConfig.muteAi,
      hasContent: !!newConfig.content
    });
    
    this.configSubject.next(newConfig);
  }

  /**
   * Determine if agent should be muted
   * Listener agent is always muted (silent)
   */
  private shouldMuteForAgent(agentId: string): boolean {
    // Listener agent is silent - always mute
    if (agentId === 'agent.listener') {
      return true;
    }
    // All other agents are active - don't mute by default
    return false;
  }

  /**
   * Reset to default (listener agent) - called when leaving step components
   * Mutes the widget (listener is silent)
   */
  resetToListener(): void {
    console.log('🔄 Resetting to listener agent (muted)');
    this.configSubject.next({ 
      ...this.defaultConfig,
      muteAi: true  // Listener is always muted
    });
  }

  /**
   * Mute AI audio (without changing agent)
   */
  mute(): void {
    const current = this.configSubject.value;
    this.updateConfig({ muteAi: true });
    console.log('🔇 AI muted');
  }

  /**
   * Unmute AI audio (without changing agent)
   */
  unmute(): void {
    const current = this.configSubject.value;
    // Only unmute if not in listener mode
    if (current.agentId !== 'agent.listener') {
      this.updateConfig({ muteAi: false });
      console.log('🔊 AI unmuted');
    } else {
      console.log('⚠️ Cannot unmute - in listener mode (silent agent)');
    }
  }

  /**
   * Toggle mute state
   */
  toggleMute(): void {
    const current = this.configSubject.value;
    if (current.muteAi) {
      this.unmute();
    } else {
      this.mute();
    }
  }

  /**
   * Get current config (synchronous)
   */
  getCurrentConfig(): GlobalAiChatConfig {
    return this.configSubject.value;
  }

  /**
   * Check if currently muted
   */
  isMuted(): boolean {
    return this.configSubject.value.muteAi;
  }

  /**
   * Clear widget (hide it)
   */
  hideWidget(): void {
    this.updateConfig({ showWidget: false });
  }

  /**
   * Show widget
   */
  showWidget(): void {
    this.updateConfig({ showWidget: true });
  }
}

